import { useState, useMemo } from "react";
import { Link } from "react-router";
import { useTranslation } from "react-i18next";
import { Pencil, Check, X } from "lucide-react";
import { Button } from "~/atoms/button";
import { Input } from "~/atoms/input";
import { updateDocumentVersion } from "~/api/v2/requests";
import { toast } from "react-toastify";
import {
  FetchError,
  type DocumentDetails,
  type DocumentDetailsVersion,
  type DocumentVersionSummary,
} from "~/api/types";
import { useDocumentVersions } from "~/api/v2/useDocumentVersions";
import { DocumentVersionStatus } from "~/config/types";
import { cn } from "~/lib/utils";
import { UnsavedChangesAlert } from "~/molecules/UnsavedChangesAlert";

// Shared layout classes to prevent layout shift between view/edit modes
const CONTAINER_CLASSES = cn("flex flex-col");
const UNDERLINE_SPACER_CLASSES = cn("h-0.5 rounded-full");
const WARNING_SPACER_CLASSES = cn("min-h-5");

// Shared hook for duplicate detection
function useDuplicateDocumentDetection({
  currentDocumentId,
  currentCategoryId,
  currentTitle,
  newTitle,
}: {
  currentDocumentId: number;
  currentCategoryId: number;
  currentTitle: string;
  newTitle: string;
}): DocumentVersionSummary | null {
  const { data: allDocumentVersions } = useDocumentVersions();

  const duplicateDocument = useMemo(() => {
    if (!allDocumentVersions || !newTitle.trim()) return null;

    const trimmedTitle = newTitle.trim();
    if (trimmedTitle === currentTitle) return null;

    return allDocumentVersions.find(
      (dv) =>
        dv.document.category.id === currentCategoryId &&
        dv.document.id !== currentDocumentId &&
        dv.title.toLowerCase() === trimmedTitle.toLowerCase() &&
        dv.status !== DocumentVersionStatus.archived,
    );
  }, [
    allDocumentVersions,
    newTitle,
    currentTitle,
    currentCategoryId,
    currentDocumentId,
  ]);

  return duplicateDocument ?? null;
}

// Shared warning component - styled like Alert warn variant for form fields
function DuplicateTitleWarning({
  duplicateDocument,
}: {
  duplicateDocument: {
    id: number;
    title: string;
    document: {
      id: number;
    };
  };
}) {
  const { t } = useTranslation();

  return (
    <p className="text-sm text-amber-600">
      {t(($) => $.modal.editDocumentTitle.duplicateWarning)}{" "}
      <Link
        to={`/documents/${duplicateDocument.document.id}?versionId=${duplicateDocument.id}&tab=metadata`}
        target="_blank"
        rel="noopener noreferrer"
        className="underline hover:text-amber-700"
      >
        {duplicateDocument.title}
      </Link>
    </p>
  );
}

interface Props {
  document: DocumentDetails;
  selectedVersion: DocumentDetailsVersion;
  onUpdate: () => void;
}

export function EditableDocumentTitle({
  document,
  selectedVersion,
  onUpdate,
}: Props) {
  const { t } = useTranslation();
  const [isEditing, setIsEditing] = useState(false);
  const [titleValue, setTitleValue] = useState(selectedVersion.title);
  const [isSaving, setIsSaving] = useState(false);
  const [isDocumentOpenInEditorError, setIsDocumentOpenInEditorError] =
    useState(false);

  const duplicateDocument = useDuplicateDocumentDetection({
    currentDocumentId: document.id,
    currentCategoryId: document.category.id,
    currentTitle: selectedVersion.title,
    newTitle: titleValue,
  });

  const handleStartEdit = () => {
    setTitleValue(selectedVersion.title);
    setIsDocumentOpenInEditorError(false);
    setIsEditing(true);
  };

  const handleCancel = () => {
    setTitleValue(selectedVersion.title);
    setIsDocumentOpenInEditorError(false);
    setIsEditing(false);
  };

  const handleSave = async () => {
    const trimmedTitle = titleValue.trim();

    // Same validation as in useDocumentEditForm: z.string().min(1)
    if (!trimmedTitle) {
      setIsDocumentOpenInEditorError(false);
      toast.error(t(($) => $.validations.required));
      return;
    }

    if (trimmedTitle === selectedVersion.title) {
      setIsEditing(false);
      return;
    }

    setIsSaving(true);
    setIsDocumentOpenInEditorError(false);
    try {
      // Same update call as DocumentMetadataPanel
      await updateDocumentVersion(selectedVersion.id, { title: trimmedTitle });
      onUpdate();
      setIsEditing(false);
      toast.success(t(($) => $.toasts.success.documentUpdated));
    } catch (error) {
      console.error("Error updating title:", error);
      if (
        error instanceof FetchError &&
        error.code === "DOCUMENT_OPEN_IN_EDITOR"
      ) {
        setIsDocumentOpenInEditorError(true);
      } else {
        toast.error(t(($) => $.toasts.error.generic));
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      void handleSave();
    } else if (e.key === "Escape") {
      handleCancel();
    }
  };

  const unsavedChangesAlert = (
    <UnsavedChangesAlert
      hasUnsavedChanges={isEditing}
      onLeave={handleCancel}
      title={t(($) => $.commons.unsavedChanges.documentTitle.title)}
      description={t(($) => $.commons.unsavedChanges.documentTitle.description)}
      cancelLabel={t(($) => $.commons.unsavedChanges.continueEditing)}
      confirmLabel={t(($) => $.commons.unsavedChanges.discardChanges)}
    />
  );

  if (!isEditing) {
    return (
      <>
        {unsavedChangesAlert}
        <div className={CONTAINER_CLASSES}>
          <div className="flex items-center gap-3">
            <h2 className="text-2xl font-semibold text-wrap wrap-break-word">
              {selectedVersion.title}
            </h2>
            <Button
              size="icon"
              variant="ghost"
              onClick={handleStartEdit}
              className="size-8 shrink-0"
            >
              <Pencil className="size-4" />
            </Button>
          </div>
          <div className={UNDERLINE_SPACER_CLASSES} />
          <div className={WARNING_SPACER_CLASSES} />
        </div>
      </>
    );
  }

  return (
    <>
      {unsavedChangesAlert}
      <div className={CONTAINER_CLASSES}>
        <div className="flex items-center gap-3">
          <div className="relative inline-block min-w-[200px]">
            <span
              className="invisible text-2xl font-semibold whitespace-pre"
              aria-hidden="true"
            >
              {titleValue || t(($) => $.modal.editDocumentTitle.placeholder)}
            </span>
            <Input
              value={titleValue}
              onChange={(e) => setTitleValue(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={t(($) => $.modal.editDocumentTitle.placeholder)}
              disabled={isSaving}
              className="absolute top-0 left-0 h-auto w-full border-none bg-transparent p-0 text-2xl font-semibold outline-none focus-visible:ring-0 focus-visible:ring-offset-0"
              autoFocus
            />
          </div>
          <Button
            size="icon"
            variant="ghost"
            onClick={handleSave}
            disabled={isSaving || !titleValue.trim()}
            className="size-8 shrink-0 text-green-600 hover:text-green-700"
          >
            <Check className="size-5" />
          </Button>
          <Button
            size="icon"
            variant="ghost"
            onClick={handleCancel}
            disabled={isSaving}
            className="text-muted-foreground hover:text-foreground size-8 shrink-0"
          >
            <X className="size-5" />
          </Button>
        </div>
        <div className={cn(UNDERLINE_SPACER_CLASSES, "bg-border")} />
        {/* Reserve space to prevent layout shift */}
        <div className={WARNING_SPACER_CLASSES}>
          {isDocumentOpenInEditorError && (
            <p className="text-sm text-red-600">
              {t(
                ($) => $.commons.workflow.documentOpenInEditorErrorDescription,
              )}
            </p>
          )}
          {duplicateDocument && (
            <DuplicateTitleWarning duplicateDocument={duplicateDocument} />
          )}
        </div>
      </div>
    </>
  );
}
